import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { AuthType } from '../types/entraAuth';

type Stage = 'email' | 'redirecting';

/**
 * LoginPage — AIR-family design (Phase 11.2, post auth-sunset)
 *
 * This is the authoritative "AIR family login" visual pattern. AIRAssist
 * adopts the same always-dark, centered-card look that DOA uses so that
 * every future AIR app can share the same silhouette.
 *
 * Local username/password auth was retired in the auth-sunset cleanup,
 * and the direct "Sign in with Microsoft (AIR)" shortcut was retired
 * afterwards — a single email-first path remains:
 *
 *     User types their work email → checkDomain() → redirect to the
 *     correct Microsoft endpoint (internal AIR tenant or external
 *     multi-tenant app). An unrecognized email surfaces a helpful
 *     "contact your administrator" banner.
 *
 * Registration was removed in Phase 9b — user provisioning is
 * administrative-only, so there is no "sign up" path on this page.
 */
const LoginPage: React.FC = () => {
  const { checkDomain, loginEntraInternal, loginEntraExternal, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/chat';

  // Stage drives which panel is visible. We always show the email field
  // on mount; the only transition is into the terminal "redirecting" panel.
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');

  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Redirect authenticated users straight to their destination.
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  // Surface errors forwarded by the OAuth callback page. We strip the
  // query param after reading it so a page refresh doesn't resurface
  // a stale error banner. StrictMode-safe: reading + stripping in one
  // effect means the second mount sees an empty search string.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get('error');
    if (err) {
      setErrorMessage(decodeURIComponent(err));
      navigate('/login', { replace: true });
    }
    // We only want this to run on first mount for the current search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isValidEmail = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail),
    [trimmedEmail],
  );

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!isValidEmail) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsEmailSubmitting(true);
    try {
      const authType: AuthType = await checkDomain(trimmedEmail.toLowerCase());

      if (authType === 'internal') {
        setStage('redirecting');
        // Full-page redirect — the browser will navigate away, so we
        // intentionally do not clear isEmailSubmitting here.
        loginEntraInternal();
        return;
      }

      if (authType === 'external') {
        setStage('redirecting');
        loginEntraExternal(trimmedEmail.toLowerCase());
        return;
      }

      // unknown
      setErrorMessage(
        'Your email is not recognized. Please contact your administrator for access.',
      );
      setIsEmailSubmitting(false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setErrorMessage(
        typeof detail === 'string'
          ? detail
          : 'We could not verify your email. Please try again.',
      );
      setIsEmailSubmitting(false);
    }
  };

  // --- render fragments --------------------------------------------------

  const errorBanner = errorMessage ? (
    <div
      role="alert"
      className="mb-4 rounded-md border border-red-700 bg-red-900/30 px-4 py-3 text-sm text-red-200"
    >
      {errorMessage}
    </div>
  ) : null;

  // Shared dark input class so the email field matches the rest of the card.
  const darkInputClass =
    'w-full rounded-md border border-gray-800 bg-gray-950 px-4 py-3 text-gray-100 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed';

  const emailInput = (
    <div>
      <label htmlFor="login-email" className="sr-only">
        Work email
      </label>
      <input
        id="login-email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (errorMessage) setErrorMessage(null);
        }}
        disabled={isEmailSubmitting}
        placeholder="Enter your work email"
        className={darkInputClass}
      />
    </div>
  );

  // Primary submit button — "Continue" takes the email through the domain
  // check, then hands off to the appropriate Microsoft endpoint.
  const continueButton = (
    <button
      type="submit"
      disabled={isEmailSubmitting || !isValidEmail}
      className="inline-flex w-full items-center justify-center rounded-md bg-primary-600 py-3 font-semibold text-white transition-colors hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {isEmailSubmitting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          <span>Checking...</span>
        </>
      ) : (
        <span>Continue</span>
      )}
    </button>
  );

  const redirectingPanel = (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <Loader2
        className="h-8 w-8 animate-spin text-primary-400"
        aria-hidden="true"
      />
      <p className="text-sm font-medium text-gray-200">
        Redirecting you to Microsoft...
      </p>
      <p className="text-xs text-gray-500">
        If this takes more than a few seconds, please refresh and try again.
      </p>
    </div>
  );

  // ----------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-950 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px]">
        <div className="rounded-2xl border border-gray-800 bg-gray-900 p-10 shadow-2xl">
          {/* Header: logo + title + tagline */}
          <div className="mb-8 text-center">
            <img
              src="/AIRlogo.png"
              alt="AIR Control Concepts"
              className="mx-auto mb-6 h-16 w-auto brightness-0 invert"
            />
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-white">AIR </span>
              <span className="text-primary-400">Assist</span>
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              Sign in with your company email
            </p>
          </div>

          {errorBanner}

          {stage === 'redirecting' ? (
            redirectingPanel
          ) : (
            <form onSubmit={handleEmailContinue} className="space-y-4">
              {emailInput}
              {continueButton}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
